import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface UserDevice {
  id: string;
  device_name: string;
  browser: string | null;
  os: string | null;
  ip_address: string | null;
  last_active_at: string;
  created_at: string;
}

const MAX_DEVICES = 3;

export function useDevices() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [devices, setDevices] = useState<UserDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDevices = async () => {
    if (!user) return;
    
    setIsLoading(true);
    const { data, error } = await supabase
      .from("user_devices")
      .select("*")
      .eq("user_id", user.id)
      .order("last_active_at", { ascending: false });

    if (error) {
      console.error("Error fetching devices:", error);
    } else {
      setDevices(data || []);
    }
    setIsLoading(false);
  };

  const registerDevice = async () => {
    if (!user) return { success: false, error: "未登录" };

    const deviceInfo = getDeviceInfo();
    
    try {
      const response = await supabase.functions.invoke("register-device", {
        body: {
          device_name: deviceInfo.name,
          browser: deviceInfo.browser,
          os: deviceInfo.os,
        },
      });

      if (response.error) {
        return { success: false, error: response.error.message };
      }

      if (response.data?.error) {
        return { success: false, error: response.data.error };
      }

      await fetchDevices();
      return { success: true };
    } catch (err) {
      console.error("Error registering device:", err);
      return { success: false, error: "注册设备失败" };
    }
  };

  const removeDevice = async (deviceId: string) => {
    const { error } = await supabase
      .from("user_devices")
      .delete()
      .eq("id", deviceId);

    if (error) {
      toast({
        title: "删除失败",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }

    toast({ title: "设备已删除" });
    await fetchDevices();
    return true;
  };

  const updateLastActive = async (deviceId: string) => {
    await supabase
      .from("user_devices")
      .update({ last_active_at: new Date().toISOString() })
      .eq("id", deviceId);
  };

  useEffect(() => {
    fetchDevices();
  }, [user]);

  return {
    devices,
    isLoading,
    maxDevices: MAX_DEVICES,
    registerDevice,
    removeDevice,
    updateLastActive,
    refetch: fetchDevices,
  };
}

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let browser = "未知浏览器";
  let os = "未知系统";
  let name = "未知设备";

  // 检测浏览器
  if (ua.includes("Chrome") && !ua.includes("Edg")) {
    browser = "Chrome";
  } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
    browser = "Safari";
  } else if (ua.includes("Firefox")) {
    browser = "Firefox";
  } else if (ua.includes("Edg")) {
    browser = "Edge";
  }

  // 检测操作系统
  if (ua.includes("Windows")) {
    os = "Windows";
    name = "Windows 电脑";
  } else if (ua.includes("Mac")) {
    os = "macOS";
    name = "Mac 电脑";
  } else if (ua.includes("iPhone")) {
    os = "iOS";
    name = "iPhone";
  } else if (ua.includes("iPad")) {
    os = "iPadOS";
    name = "iPad";
  } else if (ua.includes("Android")) {
    os = "Android";
    name = ua.includes("Mobile") ? "Android 手机" : "Android 平板";
  } else if (ua.includes("Linux")) {
    os = "Linux";
    name = "Linux 电脑";
  }

  return { name: `${name} - ${browser}`, browser, os };
}
